// Cross-cutting primitives — the scalar vocabulary and the list envelope.
//
// KEEPERS ONLY. The API types are generated (src/gen/client.ts) and re-exported
// by src/facade.ts. What stays here is what the OpenAPI document does not
// define: the scalar names the SPA validates single values against, the list
// envelope helper, and the share arithmetic. Every resource schema that used to
// live beside them is now generated from backend/crm-api/openapi.
import { z } from "zod";
import { CurrencySchema } from "./gen/client.js";
// ---------------------------------------------------------------------------
// Money — integer minor units + explicit currency, never floats.
// ---------------------------------------------------------------------------
/**
 * An amount is ALWAYS `{ amount_minor, currency }`. Minor units (cents) as an
 * integer: a float cannot represent 0.1 exactly, and revenue splits multiply,
 * so a float would drift a fraction of a cent per line and disagree with the
 * invoice by the end of a report.
 *
 * The currency comes from the generated enum, so a new currency in the document
 * reaches this scalar without a second edit.
 */
export const Money = z.object({
    amount_minor: z.int(),
    currency: CurrencySchema,
});
// ---------------------------------------------------------------------------
// Dates — ISO-8601 date strings for business dates, RFC 3339 UTC timestamps for
// audit. Never mixed. A business date has no timezone: an invoice due
// 2026-09-01 is due on that calendar day everywhere.
// ---------------------------------------------------------------------------
export const IsoDate = z.iso.date();
export const Instant = z.iso.datetime();
/** `YYYY-MM` — the bucket key reports group by. */
export const IsoMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "expected YYYY-MM");
/** ISO week key `YYYY-Www`, used by the billing lineup report. */
export const IsoWeek = z.string().regex(/^\d{4}-W(0[1-9]|[1-4][0-9]|5[0-3])$/, "expected YYYY-Www");
// ---------------------------------------------------------------------------
// Percentages — split percentages, as a decimal number with 3 places (32.5).
// Not money, so a plain number is safe; the split ARITHMETIC rounds once, at
// the end, to integer minor units.
// ---------------------------------------------------------------------------
export const Percent = z.number().min(0).max(100);
/**
 * Our share of a gross amount, in minor units. The TypeScript mirror of the
 * backend's share arithmetic — same expression, same single round at the end,
 * so a share computed on the read path cannot come out a cent away from one the
 * server computed.
 *
 * Almost every share is computed by the server and should stay that way. This
 * exists for the one case that cannot: a share of an amount the browser has
 * already converted into the report's currency, where doing the split on the
 * server would mean one round before the conversion and another after it.
 *
 * `Math.round` and the backend's HALF_UP agree on non-negative values (both
 * round half away from zero), and every amount here is non-negative:
 * `fee_minor >= 0` and `split_pct` is 0..100.
 */
export function splitMinor(grossMinor, splitPct) {
    return Math.round((grossMinor * splitPct) / 100);
}
// ---------------------------------------------------------------------------
// Identity + concurrency. Every mutable row carries `version`; a PATCH sends it
// as If-Match and a mismatch is a 409.
// ---------------------------------------------------------------------------
export const Uuid = z.uuid();
export const RecordMeta = z.object({
    id: Uuid,
    version: z.int().min(1),
    created_at: Instant,
    updated_at: Instant,
});
// ---------------------------------------------------------------------------
// List envelope — `{ items, total, limit, offset }`, identical on every
// collection. An agent that learns to page one resource can page all of them.
//
// The document declares one `*List` schema per resource, so this helper is no
// longer how the wire shape is defined. It stays because the SPA builds
// envelopes of its own — a filtered client-side page, a test fixture — and one
// definition keeps those the same shape as the server's.
// ---------------------------------------------------------------------------
export function listEnvelope(item) {
    return z.object({
        items: z.array(item),
        total: z.int().min(0),
        limit: z.int().min(1),
        offset: z.int().min(0),
    });
}
export const DEFAULT_LIST_LIMIT = 50;
export const MAX_LIST_LIMIT = 500;
// ---------------------------------------------------------------------------
// Sort — `?sort=field:asc|desc`, repeatable. The legal fields per resource are
// declared by the document (each list operation's `sort` parameter is an enum);
// this is the parsed shape the SPA holds a sort in.
// ---------------------------------------------------------------------------
export const SortDirection = z.enum(["asc", "desc"]);
export const SortSpec = z.object({
    field: z.string(),
    direction: SortDirection,
});
//# sourceMappingURL=common.js.map